import React from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LinkIcon from '@mui/icons-material/Link'

import { typePillChipProps } from '../shared/AppDrawer/chipPresets'
import { poolwerxColors } from '../../theme/colors'
import {
  extractSummary,
  stripLeadingParagraph,
  type Release,
} from '../../utils/releases'
import { formatDate } from '../../utils/formatters'
import { ReleaseBody } from './ReleaseBody'
import ReleaseIntro from './ReleaseIntro'
import PscNotesPanel from './PscNotesPanel'

export const LATEST_CHIP_LABEL = 'Latest'

interface ReleaseCardProps {
  release: Release
  expanded: boolean
  onToggle: (version: string) => void
  isLatest?: boolean
  isHighlighted?: boolean
  onCopyLink?: (release: Release) => void
  /**
   * Anchor id for hash navigation. Defaults to `v${version}` on the page; the
   * popup passes `undefined` so it does not register competing anchors.
   */
  anchorId?: string
}

const ReleaseCard: React.FC<ReleaseCardProps> = ({
  release,
  expanded,
  onToggle,
  isLatest = false,
  isHighlighted = false,
  onCopyLink,
  anchorId,
}) => {
  const hasFpBody = Boolean(release.body && release.body.trim())
  const hasPscBody = Boolean(release.internalBody && release.internalBody.trim())
  const summary = React.useMemo(() => extractSummary(release.body), [release.body])
  const bodyAfterIntro = React.useMemo(
    () => stripLeadingParagraph(release.body),
    [release.body],
  )
  const formattedDate = release.date ? formatDate(release.date) : ''

  const showLeftRail = isLatest || isHighlighted
  const bgColor = isHighlighted ? 'rgba(0,127,152,0.05)' : '#FFFFFF'

  return (
    <Accordion
      id={anchorId}
      expanded={expanded}
      onChange={() => onToggle(release.version)}
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        border: '1px solid #D7E3E7',
        ...(showLeftRail ? { borderLeft: `4px solid ${poolwerxColors.teal}` } : {}),
        borderRadius: '10px',
        bgcolor: bgColor,
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        transition:
          'background-color 0.8s ease-out, border-color 0.8s ease-out, border-left-color 0.8s ease-out',
        scrollMarginTop: 96,
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },
        '&:not(.Mui-expanded):hover': {
          borderColor: '#BFD5DC',
          bgcolor: '#FBFEFF',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          position: 'relative',
          px: { xs: 2.5, sm: 3 },
          pt: 1.25,
          pb: 0.5,
          '& .MuiAccordionSummary-content': {
            mt: 1,
            mb: 0.25,
            alignItems: 'flex-start',
            minWidth: 0,
          },
        }}
      >
        {isLatest && (
          <Chip
            label={LATEST_CHIP_LABEL}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 18,
              zIndex: 1,
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 600,
              color: '#FFFFFF',
              backgroundColor: poolwerxColors.teal,
              letterSpacing: 0.4,
              pointerEvents: 'none',
            }}
          />
        )}
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1, width: '100%' }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0, width: '100%', pr: isLatest ? '70px' : 0 }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 1,
              }}
            >
              {release.title}
            </Typography>
            <Chip {...typePillChipProps(`v${release.version}`)} />
            {onCopyLink && (
              <Tooltip title="Copy link to this release">
                <IconButton
                  size="small"
                  aria-label={`Copy link to v${release.version}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onCopyLink(release)
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {(formattedDate || summary) && (
            <Stack spacing={0.875} sx={{ mt: '-5px' }}>
              {formattedDate && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ color: 'text.secondary' }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 12 }} />
                  <Typography
                    variant="caption"
                    sx={{
                      letterSpacing: 0.2,
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}
                  >
                    {formattedDate}
                  </Typography>
                </Stack>
              )}
              <ReleaseIntro text={summary} />
            </Stack>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 2.5, sm: 3 }, pt: 0, pb: 2.5, minWidth: 0 }}>
        {hasFpBody && bodyAfterIntro && (
          <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
            <ReleaseBody body={bodyAfterIntro} decorateHeadings />
          </Box>
        )}
        {hasPscBody && (
          <PscNotesPanel body={release.internalBody as string} />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default ReleaseCard
